/**
 * NX-2737 (E12.3) — the internal ops console: self-health, calibration, error rates, for
 * SeneTek's own operating team. Admin-only end to end: the API 403s every other role and this
 * route renders that refusal honestly rather than an empty shell.
 *
 * Rendering rules: every section degrades honestly (AC6) — a short or missing series says
 * "insufficient data", never a chart implying zero. Trend series render as compact sparkline-like
 * value runs (min→max annotated) rather than a charting dependency this repo doesn't carry.
 */
import { useState } from "react";
import { Breadcrumb } from "../shell/Shell.js";
import { useApi } from "../api/useApi.js";
import { EmptyState, HonestState, Tag } from "../components.js";
import { Button } from "../components/controls.js";
import { formatDurationSeconds } from "../format.js";
import "./ops.css";

interface LagPoint {
  readonly recordedAt: string;
  readonly ageSeconds: number;
}
interface LatencyPoint {
  readonly recordedAt: string;
  readonly durationMs: number;
}
interface RouteRow {
  readonly route: string;
  readonly count: number;
  readonly p50: number;
  readonly p95: number;
  readonly errorRate: number;
}
interface OpsPayload {
  readonly windowHours: number;
  readonly sources: ReadonlyArray<{
    source: string;
    ageSeconds: number;
    stale: boolean;
    lagTrend: readonly LagPoint[];
  }>;
  readonly derivations: ReadonlyArray<{
    name: string;
    failureStreak: number;
    latencyTrend: readonly LatencyPoint[];
  }>;
  readonly routes: ReadonlyArray<RouteRow>;
  /** NX-2839: no-tenant rows, shown apart from this tenant's routes — never blended. */
  readonly unattributedRoutes: {
    readonly unauthenticated: ReadonlyArray<RouteRow>;
    readonly preScoping: ReadonlyArray<RouteRow>;
  };
  readonly calibration: ReadonlyArray<{
    projectId: string;
    name: string;
    meanRatio: number | null;
    reason: string | null;
    sampleCount: number | null;
  }>;
}

const WINDOWS = [24, 72, 168] as const;

function trendSummary(values: readonly number[], unit: string): string {
  if (values.length < 2) return "insufficient data for a trend";
  const min = Math.min(...values);
  const max = Math.max(...values);
  const last = values[values.length - 1]!;
  return `${values.length} readings · min ${min}${unit} · max ${max}${unit} · latest ${last}${unit}`;
}

/** The per-route table, shared by this tenant's routes and the two unattributed groups (NX-2839). */
function RouteTable({ rows }: { readonly rows: ReadonlyArray<RouteRow> }): React.ReactNode {
  return (
    <div className="panel pad">
      <table className="opstable">
        <thead>
          <tr>
            <th>route</th>
            <th>requests</th>
            <th>p50</th>
            <th>p95</th>
            <th>5xx rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.route} className={r.errorRate > 0.01 ? "warn" : ""}>
              <td>{r.route}</td>
              <td>{r.count}</td>
              <td>{r.p50}ms</td>
              <td>{r.p95}ms</td>
              <td>{(r.errorRate * 100).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function OpsView(): React.ReactNode {
  const [windowHours, setWindowHours] = useState<number>(24);
  const state = useApi<OpsPayload>(`/ops/self-health?windowHours=${windowHours}`);
  const crumb = <Breadcrumb items={[{ label: "Ops" }]} />;
  if (state.status === "loading") {
    return (
      <>
        {crumb}
        <div className="meta">Reading self-instrumentation…</div>
      </>
    );
  }
  if (state.status === "error") {
    return (
      <>
        {crumb}
        <EmptyState
          message={
            state.error.status === 403
              ? "The ops console is for SeneTek's operating team (admin role) only."
              : `Couldn't load self-health: ${state.error.message}`
          }
        />
      </>
    );
  }
  const d = state.envelope.data;
  return (
    <>
      {crumb}
      <div className="head">
        <h1>Ops console</h1>
      </div>
      <div className="opswin">
        {WINDOWS.map((w) => (
          <Button
            key={w}
            size="sm"
            variant={w === windowHours ? "primary" : "ghost"}
            aria-pressed={w === windowHours}
            onClick={() => setWindowHours(w)}
          >
            {w}h
          </Button>
        ))}
        <span className="cnt">internal · self-health over the selected window</span>
      </div>

      <div className="sectitle">
        <h3 className="sec">Ingestion</h3>
        <span className="cnt">per-source cursor age + lag trend (E12.1)</span>
      </div>
      <div className={`grid cols-${Math.min(3, Math.max(1, d.sources.length))} metrics`}>
        {d.sources.length === 0 && (
          <HonestState kind="nodata" detail="No source cursors recorded for this tenant yet." />
        )}
        {d.sources.map((s) => (
          <div className="metric" key={s.source}>
            <div className="lab">{s.source}</div>
            <div className="v">
              <Tag tone={s.stale ? "red" : "green"}>{s.stale ? "Stale" : "Fresh"}</Tag>
            </div>
            <div className="mc">
              <span>cursor age {formatDurationSeconds(s.ageSeconds)}</span>
            </div>
            <div className="mc">
              <span>
                {trendSummary(
                  s.lagTrend.map((p) => p.ageSeconds),
                  "s",
                )}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="sectitle">
        <h3 className="sec">Derivations</h3>
        <span className="cnt">latency trend + NX-2712 failure streak, one place</span>
      </div>
      <div className="grid cols-4 metrics">
        {d.derivations.map((der) => (
          <div className="metric" key={der.name}>
            <div className="lab">{der.name}</div>
            <div className="v">
              {der.failureStreak > 0 ? (
                <Tag tone="red">failing ×{der.failureStreak}</Tag>
              ) : (
                <Tag tone="green">Passing</Tag>
              )}
            </div>
            <div className="mc">
              <span>
                {trendSummary(
                  der.latencyTrend.map((p) => p.durationMs),
                  "ms",
                )}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="sectitle">
        <h3 className="sec">API</h3>
        <span className="cnt">per-route latency percentiles + 5xx rate</span>
      </div>
      {d.routes.length === 0 ? (
        <HonestState
          kind="nodata"
          detail="No API request metrics recorded for this tenant in this window."
        />
      ) : (
        <RouteTable rows={d.routes} />
      )}
      {d.unattributedRoutes.unauthenticated.length > 0 ? (
        <>
          <div className="sectitle">
            <h4 className="sec">Unauthenticated requests</h4>
            <span className="cnt">public routes · no tenant · kept apart from the table above</span>
          </div>
          <RouteTable rows={d.unattributedRoutes.unauthenticated} />
        </>
      ) : null}
      {d.unattributedRoutes.preScoping.length > 0 ? (
        <>
          <div className="sectitle">
            <h4 className="sec">Pre-scoping requests</h4>
            <span className="cnt">
              recorded before tenant attribution existed · attributed to no one
            </span>
          </div>
          <RouteTable rows={d.unattributedRoutes.preScoping} />
        </>
      ) : null}

      <div className="sectitle">
        <h3 className="sec">Calibration</h3>
        <span className="cnt">
          does our own stated estimation output track reality (E13.3 engine, reused)
        </span>
      </div>
      <div className={`grid cols-${Math.min(3, Math.max(1, d.calibration.length))} metrics`}>
        {d.calibration.length === 0 && (
          <HonestState kind="nodata" detail="No projects to calibrate against yet." />
        )}
        {d.calibration.map((c) =>
          c.meanRatio === null ? (
            <HonestState
              key={c.projectId}
              kind="nodata"
              title={c.name}
              detail={c.reason ?? "insufficient data"}
            />
          ) : (
            <div className="metric" key={c.projectId}>
              <div className="lab">{c.name}</div>
              <div className="v num">
                {c.meanRatio.toFixed(2)}× <small>mean actual/estimated</small>
              </div>
              <div className="mc">
                <span>{c.sampleCount} completed+estimated items</span>
              </div>
            </div>
          ),
        )}
      </div>
    </>
  );
}
